import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  closeExerciseTransaction,
  completeExerciseTransaction,
  createBlackOutDay,
  createTransaction as createExerciseTransaction,
  deleteBlackOutDay,
  deleteExerciseTransaction,
  editTransaction,
  getAllExerciseEvents,
  getBlackOutDaysForTransaction,
  getEmployeesFroAdditionToTransaction,
  getTransactionDetails,
  startExerciseTransaction,
  updateBlackOutDay,
  getExerciseLettersAdmin,
} from "../api/TransactionApisV2";
import { BlackOutDay } from "../types/Transaction";
import { queryClient } from "./client";

export function useGetExerciseTransactionDetailsToCreateEvent(
  enabled: boolean
) {
  return useQuery({
    queryKey: "getTransactionDetails",
    queryFn: async () => {
      const data = await getTransactionDetails();
      return data.data;
    },
    enabled,
  });
}

export function useGetEmployeesForAddition() {
  return useQuery({
    queryKey: "getEmployees",
    queryFn: async () => {
      const data = await getEmployeesFroAdditionToTransaction();
      return data.data;
    },
  });
}

export function useCreateExerciseTransaction() {
  return useMutation({
    mutationKey: "createTransaction",
    mutationFn: async (data: any) => {
      const response = await createExerciseTransaction(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("getAllExerciseEvents");
      queryClient.refetchQueries("getAllExerciseEvents");
    },
  });
}

export function useStartExerciseTransaction() {
  return useMutation({
    mutationKey: "startExerciseTransaction",
    mutationFn: async (data: any) => {
      const response = await startExerciseTransaction(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("getAllExerciseEvents");
      queryClient.refetchQueries("getAllExerciseEvents");
    },
  });
}

export function useCloseExerciseTransaction() {
  return useMutation({
    mutationKey: "closeExerciseTransaction",
    mutationFn: async (data: any) => {
      const response = await closeExerciseTransaction(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("getAllExerciseEvents");
      queryClient.refetchQueries("getAllExerciseEvents");
    },
  });
}

export function useCompleteExerciseTransaction() {
  return useMutation({
    mutationKey: "completeExerciseTransaction",
    mutationFn: async (data: any) => {
      const response = await completeExerciseTransaction(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("getAllExerciseEvents");
      queryClient.refetchQueries("getAllExerciseEvents");
    },
  });
}

export function useDeleteTransaction() {
  return useMutation({
    mutationKey: "deleteTransaction",
    mutationFn: async (transactionId: string) => {
      const response = await deleteExerciseTransaction(transactionId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("getAllExerciseEvents");
      queryClient.refetchQueries("getAllExerciseEvents");
    },
  });
}

export function useEditTransaction() {
  return useMutation({
    mutationKey: "editTransaction",
    mutationFn: async (data: any) => {
      const response = await editTransaction(data);
      return response.data;
    },
  });
}

export function useGetBlackOutDays() {
  return useQuery({
    queryKey: ["getBlackOutDays"],
    queryFn: async () => {
      const res = await getBlackOutDaysForTransaction();
      return res.data;
    },
  });
}

export function useGetAllExerciseEvents() {
  return useQuery({
    queryKey: ["getAllExerciseEvents"],
    queryFn: async () => {
      const res = await getAllExerciseEvents();
      return res.data;
    },
  });
}

export function useCreateBlackOutDay() {
  return useMutation({
    mutationKey: ["createBlackOutDay"],
    mutationFn: async (data: BlackOutDay) => {
      const res = await createBlackOutDay(data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["getBlackOutDays"]);
      queryClient.refetchQueries(["getBlackOutDays"]);
    },
  });
}

export function useUpdateBlackOutDay() {
  return useMutation({
    mutationKey: ["updateBlackOutDay"],
    mutationFn: async (data: BlackOutDay) => {
      const res = await updateBlackOutDay(data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["getBlackOutDays"]);
      queryClient.refetchQueries(["getBlackOutDays"]);
    },
  });
}

export function useDeleteBlackOutDay() {
  return useMutation({
    mutationKey: ["deleteBlackOutDay"],
    mutationFn: async (id: string) => {
      const res = await deleteBlackOutDay(id);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["getBlackOutDays"]);
      queryClient.refetchQueries(["getBlackOutDays"]);
    },
  });
}

export function useGetExerciseLettersAdmin(
  transactionId: string,
  employeeId: string,
  requestId?: string
) {
  return useQuery({
    queryKey: ["getExerciseLettersAdmin", transactionId, employeeId, requestId],
    queryFn: async () => {
      const res = await getExerciseLettersAdmin(
        transactionId,
        employeeId,
        requestId
      );
      return res.data ?? [];
    },
    enabled: !!transactionId && !!employeeId,
  });
}
